/**
 * DogLogStreaksWidget — Strava-style daily-log streak leaderboard.
 *
 * Reuses the `/api/care/today` payload (so no second fetch on the
 * dashboard) and renders a compact ranked list of the user's dogs by
 * their current `activity_streak_days` value — any-kind log entry
 * counts, so a meal-only day still keeps the flame burning.
 *
 * The top dog gets a gold flame + "On fire" pill. Anyone at 0 sits at
 * the bottom in muted ink with a "Start today" CTA. Tapping any row
 * opens that dog's Dog Log surface scoped to its dog_id so the user
 * can knock out today's log in a tap.
 *
 * Hidden when:
 *   • Dog Log inactive (no streak data to show).
 *   • User owns zero dogs.
 *   • Every dog is at 0 AND has never logged anything in the 14-day
 *     window (no value to surface a board full of zeros).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FlameIcon as Flame, TrophyIcon as Trophy } from "@phosphor-icons/react";
import { thumbUrl } from "@/lib/utils-app";

function StreakRow({ dog, rank, isTop }) {
  const days = dog.activity_streak_days || 0;
  const tone =
    days >= 7
      ? "text-[#C1272D]"
      : days >= 3
        ? "text-gold-dark"
        : days >= 1
          ? "text-[#3F3F3F]"
          : "text-[#9CA3AF]";

  return (
    <Link
      to={`/dog-log?dog_id=${dog.id}`}
      data-testid={`streak-row-${dog.id}`}
      className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] transition-colors"
    >
      <span
        className={`flex-shrink-0 w-6 text-[11px] font-bold tabular-nums text-center ${
          isTop ? "text-gold-dark" : "text-[#9CA3AF]"
        }`}
      >
        {isTop ? <Trophy size={14} weight="fill" className="inline" /> : `#${rank}`}
      </span>
      {dog.photo ? (
        <img
          src={thumbUrl(dog.photo)}
          alt=""
          className="h-9 w-9 rounded-full object-cover flex-shrink-0 ring-1 ring-[#E5E5E5]"
        />
      ) : (
        <span className="h-9 w-9 rounded-full bg-[#FBF7EC] ring-1 ring-[#E8D9A6] flex-shrink-0" />
      )}
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-semibold text-ink truncate">{dog.name}</span>
        <span className="block text-[11px] text-[#6B6B6B] truncate">
          {dog.breed || "—"}
        </span>
      </span>
      <span className={`flex items-center gap-1 ${tone}`}>
        <Flame size={16} weight={days > 0 ? "fill" : "regular"} />
        <span className="font-spectral text-xl tabular-nums leading-none">{days}</span>
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold ml-0.5">
          {days === 1 ? "day" : "days"}
        </span>
      </span>
      {isTop && days >= 3 && (
        <span
          className="hidden sm:inline-flex items-center rounded-full bg-gold/15 text-gold-dark text-[9px] font-bold uppercase tracking-[0.16em] px-2 py-1 flex-shrink-0"
          data-testid="streak-top-pill"
        >
          On fire
        </span>
      )}
    </Link>
  );
}

export default function DogLogStreaksWidget() {
  const [dogs, setDogs] = useState(null); // null = loading, [] = empty, [{...}] = data
  const [active, setActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/care/today", { silent: true })
      .then((r) => {
        if (cancelled) return;
        setActive(!!r.data?.active);
        setDogs(r.data?.dogs || []);
      })
      .catch(() => { if (!cancelled) { setActive(false); setDogs([]); } });
    return () => { cancelled = true; };
  }, []);

  if (dogs === null) return null; // loading
  if (!active) return null;
  if (dogs.length === 0) return null;

  // Sort by streak desc; tie-broken by today_total desc so an active
  // logger today edges out a stale tie. Stable enough to give a
  // satisfying "I just took the lead" moment after a quick log.
  const ranked = [...dogs].sort((a, b) => {
    const sa = a.activity_streak_days || 0;
    const sb = b.activity_streak_days || 0;
    if (sb !== sa) return sb - sa;
    return (b.today_total || 0) - (a.today_total || 0);
  });

  // Don't show a board of all-zero streaks for fresh accounts — they'll
  // see the empty state in the Today widget already.
  const anyStreak = ranked.some((d) => (d.activity_streak_days || 0) > 0);
  if (!anyStreak) return null;

  const topStreak = ranked[0]?.activity_streak_days || 0;

  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] mb-8 overflow-hidden"
      data-testid="dog-log-streaks-widget"
    >
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#FEE2E2] flex-shrink-0">
            <Flame size={18} weight="fill" className="text-[#C1272D]" />
          </span>
          <div className="min-w-0">
            <h2 className="font-spectral text-xl sm:text-2xl tracking-tight leading-tight">
              Dog Log streaks
            </h2>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5">
              {topStreak >= 7
                ? `${ranked[0].name} is on a ${topStreak}-day streak. Keep it going.`
                : topStreak >= 3
                  ? `${ranked[0].name} leads the pack. Log today to extend the streak.`
                  : "Log any entry today to start a streak."}
            </p>
          </div>
        </div>
        <Link
          to="/dog-log"
          className="hidden sm:inline-block text-[12px] font-semibold text-ink hover:text-crimson transition-colors flex-shrink-0"
          data-testid="streaks-open-full"
        >
          Open Dog Log →
        </Link>
      </div>
      <div className="border-t border-[#E5E5E5] divide-y divide-[#E5E5E5]">
        {ranked.map((dog, idx) => (
          <StreakRow
            key={dog.id}
            dog={dog}
            rank={idx + 1}
            isTop={idx === 0 && (dog.activity_streak_days || 0) > 0}
          />
        ))}
      </div>
    </div>
  );
}
